import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import CookieConsent from "@/components/CookieConsent";
import i18n, {
  SUPPORTED_LANGUAGES,
  extractLangFromPath,
  resolveInitialLanguage,
  type SupportedLanguage,
} from "@/i18n";

// Lazy load toast components to reduce initial bundle size
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const LinkedInInsightTag = lazy(() => import("@/components/LinkedInInsightTag"));

// Lazy load pages for code splitting
const ServiceSelector = lazy(() => import("./pages/ServiceSelector"));
const CybersecurityPage = lazy(() => import("./pages/CybersecurityPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const IntegratedPage = lazy(() => import("./pages/IntegratedPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Cyber Services
const VulnerabilityAnalysis = lazy(() => import("./pages/services/cyber/VulnerabilityAnalysis"));
const PenetrationTesting = lazy(() => import("./pages/services/cyber/PenetrationTesting"));
const TrainingDevelopment = lazy(() => import("./pages/services/cyber/TrainingDevelopment"));
const SecurityAudit = lazy(() => import("./pages/services/cyber/SecurityAudit"));
const Hardening = lazy(() => import("./pages/services/cyber/Hardening"));
const SocialEngineering = lazy(() => import("./pages/services/cyber/SocialEngineering"));
const RemediationSupport = lazy(() => import("./pages/services/cyber/RemediationSupport"));
const DigitalForensics = lazy(() => import("./pages/services/cyber/DigitalForensics"));
const RedTeam = lazy(() => import("./pages/services/cyber/RedTeam"));
const CISOaaS = lazy(() => import("./pages/services/cyber/CISOaaS"));
const RegulatoryCompliance = lazy(() => import("./pages/services/cyber/RegulatoryCompliance"));

// Legal Services
const EcommerceDataPrivacy = lazy(() => import("./pages/services/legal/EcommerceDataPrivacy"));
const TermsCompliance = lazy(() => import("./pages/services/legal/TermsCompliance"));
const DigitalPlatforms = lazy(() => import("./pages/services/legal/DigitalPlatforms"));
const CorporateGovernance = lazy(() => import("./pages/services/legal/CorporateGovernance"));
const BusinessExpansion = lazy(() => import("./pages/services/legal/BusinessExpansion"));
const ProductAnalysis = lazy(() => import("./pages/services/legal/ProductAnalysis"));
const NotarialServices = lazy(() => import("./pages/services/legal/NotarialServices"));

// Productized Offers
const ExecutiveDiagnostic = lazy(() => import("./pages/offers/ExecutiveDiagnostic"));
const TechnicalAssessment = lazy(() => import("./pages/offers/TechnicalAssessment"));
const TransitionRetainer = lazy(() => import("./pages/offers/TransitionRetainer"));
const LegalOperationPack = lazy(() => import("./pages/offers/LegalOperationPack"));

// Blog
const BlogPage = lazy(() => import("./pages/BlogPage"));
const NrpCooperativas = lazy(() => import("./pages/blog/NrpCooperativas"));
const SecurityAuditProcedures = lazy(() => import("./pages/blog/SecurityAuditProcedures"));
const DelegadoProteccionDatos = lazy(() => import("./pages/blog/DelegadoProteccionDatos"));

// Legal / Privacy
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const DataDeletionPage = lazy(() => import("./pages/DataDeletionPage"));
const ArcoPolPage = lazy(() => import("./pages/ArcoPolPage"));

const queryClient = new QueryClient();

/**
 * Bootstrap the language prefix BEFORE React Router mounts.
 *
 * If the visitor hits a path without /es or /en (e.g. "/", "/contact",
 * "/privacy"), rewrite the URL in-place to include the resolved language
 * prefix. BrowserRouter is then mounted with `basename={'/' + lang}` so
 * every <Link to="/foo"> automatically resolves to "/<lang>/foo" without
 * touching the rest of the codebase.
 */
const bootstrapLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'es';

  const existing = extractLangFromPath(window.location.pathname);
  if (existing) return existing;

  const lang = resolveInitialLanguage();
  const pathname =
    window.location.pathname === '/' ? '' : window.location.pathname;
  const newUrl = `/${lang}${pathname}${window.location.search}${window.location.hash}`;
  window.history.replaceState(null, '', newUrl);
  return lang;
};

const activeLang = bootstrapLanguage();

// Keep i18n in sync with the URL prefix on first paint.
if (i18n.language !== activeLang) {
  void i18n.changeLanguage(activeLang);
}

const AppRoutes = () => {
  // Re-sync i18n if the URL lang ever changes inside the same session
  // (e.g. user pastes a /en/... link while on /es/...).
  useEffect(() => {
    const lang = extractLangFromPath(window.location.pathname);
    if (lang && i18n.language !== lang) {
      void i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
    }
  });

  return (
    <Routes>
      <Route path="/" element={<ServiceSelector />} />
      <Route path="/cybersecurity" element={<CybersecurityPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/integrated" element={<IntegratedPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Cyber Services */}
      <Route path="/cybersecurity/vulnerability-analysis" element={<VulnerabilityAnalysis />} />
      <Route path="/cybersecurity/penetration-testing" element={<PenetrationTesting />} />
      <Route path="/cybersecurity/training-development" element={<TrainingDevelopment />} />
      <Route path="/cybersecurity/security-audit" element={<SecurityAudit />} />
      <Route path="/cybersecurity/hardening" element={<Hardening />} />
      <Route path="/cybersecurity/social-engineering" element={<SocialEngineering />} />
      <Route path="/cybersecurity/remediation-support" element={<RemediationSupport />} />
      <Route path="/cybersecurity/digital-forensics" element={<DigitalForensics />} />
      <Route path="/cybersecurity/red-team" element={<RedTeam />} />
      <Route path="/cybersecurity/ciso-as-service" element={<CISOaaS />} />
      <Route path="/cybersecurity/regulatory-compliance" element={<RegulatoryCompliance />} />

      {/* Legal Services */}
      <Route path="/legal/ecommerce-data-privacy" element={<EcommerceDataPrivacy />} />
      <Route path="/legal/terms-compliance" element={<TermsCompliance />} />
      <Route path="/legal/digital-platforms" element={<DigitalPlatforms />} />
      <Route path="/legal/corporate-governance" element={<CorporateGovernance />} />
      <Route path="/legal/business-expansion" element={<BusinessExpansion />} />
      <Route path="/legal/product-analysis" element={<ProductAnalysis />} />
      <Route path="/legal/notarial-services" element={<NotarialServices />} />

      {/* Productized Offers */}
      <Route path="/offers/executive-diagnostic" element={<ExecutiveDiagnostic />} />
      <Route path="/offers/technical-assessment" element={<TechnicalAssessment />} />
      <Route path="/offers/transition-retainer" element={<TransitionRetainer />} />
      <Route path="/offers/legal-operation-pack" element={<LegalOperationPack />} />

      {/* Blog */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/regulacion-cooperativas-preparacion-nrp-23-24-32" element={<NrpCooperativas />} />
      <Route path="/blog/security-audit-procedures-guide" element={<SecurityAuditProcedures />} />
      <Route path="/blog/delegado-proteccion-datos-personales-el-salvador" element={<DelegadoProteccionDatos />} />

      {/* Privacy / Terms / Data Deletion */}
      <Route path="/privacidad" element={<Navigate to="/privacy" replace />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/terminos" element={<Navigate to="/terms" replace />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/arco-pol" element={<ArcoPolPage />} />
      <Route path="/arco-politicas" element={<Navigate to="/arco-pol" replace />} />
      <Route path="/eliminacion-de-datos" element={<Navigate to="/data-deletion" replace />} />

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter basename={`/${activeLang}`}>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
          <CookieConsent />
          <LinkedInInsightTag />
        </Suspense>
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

// Re-export for callers that previously imported from "./i18n"
export { SUPPORTED_LANGUAGES };

export default App;
